import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Pencil, Trash2 } from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import './business.css';

const CustomerGroupsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [editId, setEditId] = useState(null);
  const [memberGroupId, setMemberGroupId] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, m] = await Promise.all([api.customerGroups(), api.listGroupMembers()]);
      setGroups(g?.groups || []);
      setMembers(m?.members || []);
    } catch (err) {
      showToast(err.message || t('business.customerGroups.toast.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const saveGroup = async () => {
    if (!groupName.trim()) {
      showToast(t('business.customerGroups.toast.enterGroupName'), 'warning');
      return;
    }
    setBusy(true);
    try {
      if (editId) {
        await api.updateCustomerGroup(editId, groupName.trim());
        showToast(t('business.customerGroups.toast.groupUpdated'), 'success');
      } else {
        await api.createCustomerGroup(groupName.trim());
        showToast(t('business.customerGroups.toast.groupSaved'), 'success');
      }
      setGroupName('');
      setEditId(null);
      await load();
    } catch (err) {
      showToast(err.message || t('business.customerGroups.toast.saveGroupFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (g) => {
    setEditId(g.id);
    setGroupName(g.name);
  };

  const removeGroup = async (g) => {
    if (!window.confirm(t('business.customerGroups.deleteConfirm', { name: g.name }))) return;
    setBusy(true);
    try {
      await api.deleteCustomerGroup(g.id);
      showToast(t('business.customerGroups.toast.groupDeleted'), 'success');
      if (editId === g.id) {
        setEditId(null);
        setGroupName('');
      }
      await load();
    } catch (err) {
      showToast(err.message || t('business.customerGroups.toast.deleteGroupFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveMember = async () => {
    if (!memberGroupId || !memberEmail.trim()) {
      showToast(t('business.customerGroups.toast.selectGroupAndEmail'), 'warning');
      return;
    }
    setBusy(true);
    try {
      await api.addGroupMember(memberGroupId, memberEmail.trim());
      showToast(t('business.customerGroups.toast.customerAdded'), 'success');
      setMemberEmail('');
      await load();
    } catch (err) {
      showToast(err.message || t('business.customerGroups.toast.addCustomerFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (m) => {
    setBusy(true);
    try {
      await api.removeGroupMember(m.groupId, m.email);
      showToast(t('business.customerGroups.toast.customerRemoved'), 'success');
      await load();
    } catch (err) {
      showToast(err.message || t('business.customerGroups.toast.removeCustomerFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="q2c-biz">
      <div className="q2c-biz-header">
        <h2>{t('business.customerGroups.title')}</h2>
      </div>

      <div className="q2c-biz-info">
        {t('business.customerGroups.info')}
      </div>

      <div className="q2c-biz-groups-grid">
        <div className="q2c-biz-panel">
          <div className="q2c-biz-panel-title">{editId ? t('business.customerGroups.editGroup') : t('business.customerGroups.addGroups')}</div>
          <Form.Group className="mb-2">
            <Form.Label>{t('business.customerGroups.name')}</Form.Label>
            <Form.Control
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t('business.customerGroups.groupNamePlaceholder')}
            />
          </Form.Group>
          <div className="d-flex gap-2">
            <Button size="sm" variant="primary" disabled={busy} onClick={saveGroup}>
              {editId ? t('business.customerGroups.updateGroup') : t('business.customerGroups.saveGroup')}
            </Button>
            {editId && (
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={busy}
                onClick={() => {
                  setEditId(null);
                  setGroupName('');
                }}
              >
                {t('common.cancel')}
              </Button>
            )}
          </div>
        </div>

        <div className="q2c-biz-panel">
          <div className="q2c-biz-panel-title">{t('business.customerGroups.groups')}</div>
          {groups.length === 0 ? (
            <div className="text-muted small py-2">{t('business.customerGroups.noGroups')}</div>
          ) : (
            <div className="q2c-biz-table-wrap">
              <table className="q2c-biz-table">
                <thead>
                  <tr>
                    <th>{t('business.customerGroups.colName')}</th>
                    <th>{t('business.customerGroups.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.id}>
                      <td>{g.name}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="warning"
                            className="d-inline-flex align-items-center gap-1"
                            onClick={() => startEdit(g)}
                            disabled={busy}
                          >
                            <Pencil size={12} /> {t('business.customerGroups.edit')}
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            className="d-inline-flex align-items-center gap-1"
                            onClick={() => removeGroup(g)}
                            disabled={busy}
                          >
                            <Trash2 size={12} /> {t('business.customerGroups.delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="q2c-biz-panel">
          <div className="q2c-biz-panel-title">{t('business.customerGroups.addCustomers')}</div>
          <Form.Group className="mb-2">
            <Form.Label>{t('business.customerGroups.group')}</Form.Label>
            <Form.Select value={memberGroupId} onChange={(e) => setMemberGroupId(e.target.value)}>
              <option value="">{t('business.customerGroups.selectGroup')}</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>{t('business.customerGroups.customerEmail')}</Form.Label>
            <Form.Control
              type="email"
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              placeholder={t('business.customerGroups.emailPlaceholder')}
            />
          </Form.Group>
          <Button size="sm" variant="primary" disabled={busy} onClick={saveMember}>
            {t('business.customerGroups.saveCustomer')}
          </Button>
        </div>

        <div className="q2c-biz-panel">
          <div className="q2c-biz-panel-title">{t('business.customerGroups.customersTitle')}</div>
          {members.length === 0 ? (
            <div className="text-muted small py-2">{t('business.customerGroups.noMembers')}</div>
          ) : (
            <div className="q2c-biz-table-wrap">
              <table className="q2c-biz-table">
                <thead>
                  <tr>
                    <th>{t('business.customerGroups.colGroup')}</th>
                    <th>{t('business.customerGroups.colEmail')}</th>
                    <th>{t('business.customerGroups.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={`${m.groupId}:${m.email}`}>
                      <td>{m.groupName}</td>
                      <td>{m.email}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="danger"
                          className="d-inline-flex align-items-center gap-1"
                          onClick={() => removeMember(m)}
                          disabled={busy}
                        >
                          <Trash2 size={12} /> {t('business.customerGroups.delete')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerGroupsPage;
